import { supabase } from '../lib/supabase';
import cacheService, { CACHE_TTL } from './cacheService';
import { sendPasswordResetEmail } from './emailService';

export const authService = {
  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ 
        email, 
        password 
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { 
        data: null, 
        error: { message: error?.message || 'Failed to sign in. Please try again.' } 
      };
    }
  },

  async signUp(email, password, profileData) {
    try {
      // Get the current application URL for email confirmation redirect
      // Enforce HTTPS in production
      const origin = window.location?.origin;
      const secureOrigin = origin?.startsWith('https://') || origin?.includes('localhost')
        ? origin
        : `https://${window.location?.host}`;
      const redirectUrl = `${secureOrigin}/auth/confirm`;
      
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: profileData?.fullName,
            username: profileData?.username,
            role: 'user',
            avatar_url: profileData?.avatarUrl || '',
            address: profileData?.address || '',
            city: profileData?.city || '',
            state: profileData?.state || '',
            zip_code: profileData?.zipCode || '',
            skills: Array.isArray(profileData?.skills) ? profileData?.skills?.join(',') : '',
            keywords: profileData?.keywords || ''
          }
        }
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { 
        data: null, 
        error: { message: error?.message || 'Failed to create account. Please try again.' } 
      };
    }
  },

  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { 
        error: { message: error?.message || 'Failed to sign out. Please try again.' } 
      };
    }
  },

  async getCurrentUser() {
    try {
      const { data: { user }, error } = await supabase?.auth?.getUser();
      if (error) throw error;
      return { user, error: null };
    } catch (error) {
      console.error('Get user error:', error);
      return { 
        user: null, 
        error: { message: error?.message || 'Failed to get user.' } 
      };
    }
  },

  async getUserProfile(userId) {
    try {
      const cacheKey = `user_profile:${userId}`;
      
      return await cacheService?.getOrSet(
        cacheKey,
        async () => {
          const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
          
          if (error) throw error;
          
          return {
            id: data?.id,
            email: data?.email,
            fullName: data?.full_name,
            username: data?.username,
            role: data?.role,
            avatarUrl: data?.avatar_url,
            address: data?.address,
            city: data?.city,
            state: data?.state,
            zipCode: data?.zip_code,
            skills: data?.skills,
            keywords: data?.keywords,
            phoneNumber: data?.phone_number,
            createdAt: data?.created_at,
            updatedAt: data?.updated_at,
            accountType: data?.account_type,
            businessName: data?.business_name,
            businessDescription: data?.business_description,
            businessCategory: data?.business_category,
            storefrontSlug: data?.storefront_slug
          };
        },
        CACHE_TTL?.USER_PROFILE
      );
    } catch (error) {
      console.error('Get user profile error:', error);
      return { 
        user: null, 
        error: { message: error?.message || 'Failed to get user profile.' } 
      };
    }
  },

  async updateProfile(userId, updates) {
    try {
      const dbUpdates = {
        full_name: updates?.fullName,
        username: updates?.username,
        avatar_url: updates?.avatarUrl,
        address: updates?.address,
        city: updates?.city,
        state: updates?.state,
        zip_code: updates?.zipCode,
        skills: updates?.skills,
        keywords: updates?.keywords,
        phone_number: updates?.phoneNumber,
        updated_at: new Date()?.toISOString()
      };

      Object.keys(dbUpdates)?.forEach(key => {
        if (dbUpdates?.[key] === undefined) {
          delete dbUpdates?.[key];
        }
      });

      const { data, error } = await supabase?.from('user_profiles')?.update(dbUpdates)?.eq('id', userId)?.select()?.single();
      
      if (error) throw error;
      
      // Invalidate cache
      cacheService?.delete(`user_profile:${userId}`);
      
      return { data, error: null };
    } catch (error) {
      console.error('Update profile error:', error);
      return { 
        data: null, 
        error: { message: error?.message || 'Failed to update profile.' } 
      };
    }
  },

  async getProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      
      // Handle case where profile doesn't exist yet (PGRST116)
      if (error && error?.code === 'PGRST116') {
        return { data: null, error: null };
      }
      
      if (error) throw error;

      const camelCaseData = {
        id: data?.id,
        email: data?.email,
        fullName: data?.full_name,
        username: data?.username,
        role: data?.role,
        avatarUrl: data?.avatar_url,
        address: data?.address,
        city: data?.city,
        state: data?.state,
        zipCode: data?.zip_code,
        skills: data?.skills,
        keywords: data?.keywords,
        phoneNumber: data?.phone_number,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };

      return { data: camelCaseData, error: null };
    } catch (error) {
      console.error('Get profile error:', error);
      return { 
        data: null, 
        error: { message: error?.message || 'Failed to get profile. Please try again.' } 
      };
    }
  },

  async resendConfirmationEmail(email) {
    try {
      const origin = window.location?.origin;
      const secureOrigin = origin?.startsWith('https://') || origin?.includes('localhost')
        ? origin
        : `https://${window.location?.host}`;
      const { data, error } = await supabase?.auth?.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${secureOrigin}/auth/confirm`
        }
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Resend confirmation email error:', error);
      return { 
        data: null, 
        error: { message: error?.message || 'Failed to resend confirmation email. Please try again.' } 
      };
    }
  },

  async resetPassword(email) {
    try {
      const origin = window.location?.origin;
      const secureOrigin = origin?.startsWith('https://') || origin?.includes('localhost')
        ? origin
        : `https://${window.location?.host}`;
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${secureOrigin}/auth/confirm`
      });
      if (error) throw error;

      // Send branded password reset email notification (non-blocking)
      // Note: Supabase also sends its own reset email; this sends a supplementary branded one
      sendPasswordResetEmail(email, null, {
        resetUrl: `${secureOrigin}/auth/confirm`,
        expiresIn: '1 hour'
      })?.catch(err => console.warn('Branded password reset email failed (non-blocking):', err?.message));

      return { data, error: null };
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        data: null,
        error: { message: error?.message || 'Failed to send password reset email. Please try again.' }
      };
    }
  }
};
function getUserProfile(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getUserProfile is not implemented yet.', args);
  return null;
}

export { getUserProfile };